import { useState, useEffect } from "react";
import { BsSearch } from "react-icons/bs";
import MainInput from "../ui/MainInput/MainInput";
import "./FrontSearchInputs.scss";
import { GiCommercialAirplane } from "react-icons/gi";
import { MdFlightTakeoff, MdFlightLand, MdSmartToy } from "react-icons/md";
import { RxCalendar } from "react-icons/rx";
import { IoPerson } from "react-icons/io5";
import Button from "../ui/Button/Button";
import { useDispatch, useSelector } from "react-redux";
import { completeSearch, selectSearchFilters, setSearchOption, setSelectedDestination, setPreferredCategory, setActiveMode } from "../../reducers/searchSlice";
import { fetchHolidays } from "../../reducers/propertiesSlice";
import { setAlternativeRegionName } from "../../reducers/offersSlice";
import { fetchFares } from "../../reducers/flightSlice";
import { setView } from "../../reducers/systemSlice";
import AirportIataSelect from "../search/AirportIataSelect";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { getHost, getDurationLabel, travelerParamsFromSelection } from "../../reducers/utils";
import { AppViews } from "../../utils";
import { useIntl } from "react-intl";
import { useKIshaVisible } from "../../hooks/useKIshaVisible";
import { withKIshaParam } from "../../utils/kishaVisibility";

const FrontSearchInputs = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  axios.defaults.headers.common["AgencyID"] = window.configData.AGENCYID;
  axios.defaults.headers.common["Version"] = import.meta.env.VITE_APP_VERSION;
  axios.defaults.headers.common["App"] = window.configData.APP;

  const activeMode = useSelector((state) => state.search.activeMode);

  // Lade Schulferien + Feiertage beim Mount
  useEffect(() => {
    dispatch(fetchHolidays());
  }, [dispatch]);

  // Auto-Advance: Track which dropdown should be open
  // null = none, "search" | "airport" | "date" | "travelers"
  const [activeDropdown, setActiveDropdown] = useState(null);

  const searchParameters = useSelector(completeSearch);
  const sessionId = useSelector((state) => state.system.sessionId);
  const preferredCategory = useSelector((state) => state.search.preferredCategory);
  const agencyproperties = useSelector((state) => state.properties.agencyproperties);
  const kishaVisible = useKIshaVisible();

  const { searchRegions, searchAirports, searchDate, searchPersons } = useSelector(selectSearchFilters);

  const handleSearchChange = (value, option) => {
    dispatch(setSearchOption({ property: "searchRegions", data: option }));
    // alternativeRegionName nur zurücksetzen wenn Destination geleert oder reguläre Region gewählt wird
    if (!option || option.length === 0 || option[0]?.RegionID || option[0]?.RegionGroupID || option[0]?.CityID || option[0]?.HotelCodes) {
      dispatch(setAlternativeRegionName(""));
    }
    // Auto-Advance: Nach Zielauswahl -> Flughafen (bei PAC) oder Datum (bei ACC)
    // NUR wenn das nächste Feld noch leer ist!
    if (option && option.length > 0) {
      setTimeout(() => {
        if (activeMode === "PAC" || activeMode === "FLT") {
          // Nur zu Airport wenn noch kein Flughafen gewählt
          if (!searchAirports || searchAirports.length === 0) {
            setActiveDropdown("airport");
          }
        } else {
          // ACC/LTA: Nur zu Datum wenn noch kein Datum gewählt
          if (!searchDate?.start) {
            setActiveDropdown("date");
          }
        }
      }, 300);
    }
  };

  const handleAirportChange = (value, option) => {
    dispatch(setSearchOption({ property: "searchAirports", data: option }));
    // Auto-Advance: Nach Flughafenauswahl -> Datum
    // NUR wenn noch kein Datum gewählt ist!
    if (option && option.length > 0) {
      setTimeout(() => {
        if (!searchDate?.start) {
          setActiveDropdown("date");
        }
      }, 300);
    }
  };

  const handleDateChange = (value, option) => {
    if (!option.value.duration) {
      option.value.duration = (searchParameters.duration?.value ? searchParameters.duration.value : 7) * 1;
    }
    dispatch(setSearchOption({ property: "searchDate", data: option.value }));
    dispatch(setSearchOption({ property: "durationDelta", data: option.value?.durationRange }));

    // Auto-Advance: Nach Datumsauswahl -> Reisende
    // Öffne travelers popup wenn Datum vollständig ausgewählt wurde
    if (option.value.start && option.value.end) {
      // Prüfe ob noch keine Reisenden ausgewählt wurden
      if (!searchPersons || !searchPersons.adults) {
        setTimeout(() => {
          setActiveDropdown("travelers");
        }, 300);
      }
    }
  };

  const handleTravelersChange = (value, option) => {
    dispatch(setSearchOption({ property: "searchPersons", data: option.value }));
    // Nach Reisenden-Auswahl: Kein weiteres Dropdown
    setActiveDropdown(null);
  };

  // Handler wenn ein Dropdown geschlossen wird (z.B. durch Klick außerhalb)
  const handleDropdownClose = (dropdownType) => {
    if (activeDropdown === dropdownType) {
      setActiveDropdown(null);
    }
  };

  // Dynamische Tabs basierend auf agencyproperties
  const tabs = [
    { mode: "PAC", labelId: "packageHolidays" },
    { mode: "ACC", labelId: "hotelsOnly" },
  ];
  if (agencyproperties?.search_mode_flt) tabs.push({ mode: "FLT", labelId: "flightsOnly" });
  if (agencyproperties?.search_mode_lta) tabs.push({ mode: "LTA", labelId: "experienceTravel" });

  // Dynamische Badges: Einzel-Properties ODER premiumSearch (Widget/Agency) zeigt alle
  const premiumSearchAll = useSelector((state) => state.system.premiumSearch) || agencyproperties?.premiumSearch;
  const badges = [];
  if (agencyproperties?.badge_luxury || premiumSearchAll) badges.push({ cat: "luxury", icon: "✨", titleId: "luxury" });
  if (agencyproperties?.badge_standard || premiumSearchAll) badges.push({ cat: "standard", icon: "⭐", titleId: "standard" });
  if (agencyproperties?.badge_lowcost || premiumSearchAll) badges.push({ cat: "lowcost", icon: "💰", titleId: "lowCost" });

  const handleModeChange = (mode) => {
    // PAC/ACC → FLT: Suchdaten in FLT-Format mappen
    if (mode === "FLT" && activeMode !== "FLT") {
      if (searchAirports?.length > 0) {
        dispatch(setSearchOption({ property: "stations", data: [searchAirports[0], { value: "", label: "" }] }));
      }
      if (searchDate?.start) {
        const [d, m, y] = searchDate.start.split(".");
        const from = `${y}-${m}-${d}`;
        let until;
        if (searchDate.end) {
          const [d2, m2, y2] = searchDate.end.split(".");
          until = `${y2}-${m2}-${d2}`;
        }
        dispatch(setSearchOption({ property: "travelDate", data: { from, until } }));
      }
      if (searchPersons?.adults) {
        const val = searchPersons.adults + "";
        const labels = { 1: "1 Erwachsener", 2: "2 Erwachsene", 3: "3 Erwachsene", 4: "4 Erwachsene" };
        dispatch(setSearchOption({ property: "adults", data: { value: val, label: labels[val] || val + " Erwachsene" } }));
      }
    }

    dispatch(setActiveMode(mode));
    // Bei ACC/LTA müssen die Flughäfen geleert werden (kein Flug)
    if (mode === "ACC" || mode === "LTA") {
      dispatch(setSearchOption({ property: "searchAirports", data: [] }));
    }
  };

  const handleCategorySelect = (category) => {
    // Toggle: If same category clicked, deselect it
    dispatch(setPreferredCategory(preferredCategory === category ? "" : category));
  };

  return (
    <div className="front_search-search_container">
      <div className="front_search-search_container-header">
        {tabs.map((tab) => (
          <p key={tab.mode} className={activeMode === tab.mode ? "active" : ""} onClick={() => handleModeChange(tab.mode)}>
            {intl.formatMessage({ id: tab.labelId })}
          </p>
        ))}
      </div>

      <div className="front_search-search_container-body">
        {/* ===== FLT Mode: Flight-specific inputs ===== */}
        {activeMode === "FLT" ? (
          <FlightSearchInputs dispatch={dispatch} intl={intl} />
        ) : activeMode === "LTA" ? (
          <IndividualSearchInputs dispatch={dispatch} intl={intl} />
        ) : (
          <>
            {/* ===== PAC/ACC Mode: Standard inputs ===== */}
            <div className="front_search-search_container-body_inputs">
              <MainInput
                type="search"
                popUpView={true}
                value={searchRegions}
                textPlaceholder={intl.formatMessage({ id: "destinationOrHotel" })}
                icon={<BsSearch />}
                onChange={handleSearchChange}
                activeMode={activeMode}
                forceOpen={activeDropdown === "search"}
                onOpenChange={(isOpen) => !isOpen && handleDropdownClose("search")}
              />

              {activeMode === "PAC" && (
                <MainInput
                  type="airport"
                  popUpView={true}
                  value={searchAirports}
                  textPlaceholder={intl.formatMessage({ id: "departureAirport" })}
                  icon={<GiCommercialAirplane />}
                  onChange={handleAirportChange}
                  forceOpen={activeDropdown === "airport"}
                  onOpenChange={(isOpen) => !isOpen && handleDropdownClose("airport")}
                />
              )}

              <MainInput
                type="date"
                popUpView={true}
                textPlaceholder={intl.formatMessage({ id: "travelPeriod" })}
                icon={<RxCalendar />}
                onChange={handleDateChange}
                forceOpen={activeDropdown === "date"}
                onOpenChange={(isOpen) => !isOpen && handleDropdownClose("date")}
              />

              <MainInput
                type="travelers"
                popUpView={true}
                textPlaceholder={intl.formatMessage({ id: "travelersAndRooms" })}
                icon={<IoPerson />}
                onChange={handleTravelersChange}
                forceOpen={activeDropdown === "travelers"}
                onOpenChange={(isOpen) => !isOpen && handleDropdownClose("travelers")}
              />
            </div>

            {/* Premium Category Badges - Dynamisch aus agencyproperties */}
            {badges.length > 0 && (
              <div className="premium-badges-vertical">
                {badges.map((b) => (
                  <span
                    key={b.cat}
                    className={`premium-badge-icon ${preferredCategory === b.cat ? "active" : ""}`}
                    onClick={() => handleCategorySelect(b.cat)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === "Enter" && handleCategorySelect(b.cat)}
                    title={intl.formatMessage({ id: b.titleId })}
                  >
                    {b.icon}
                  </span>
                ))}
              </div>
            )}
          </>
        )}

        {/* KIsha Button für PAC/ACC */}
        {activeMode !== "FLT" && activeMode !== "LTA" && kishaVisible && (
          <span
            className="premium-badge-icon"
            role="button"
            tabIndex="0"
            onClick={() => {
              const currentUrl = agencyproperties?.bookingurl || window.location.origin + window.location.pathname;
              // Neuer Tab = leerer sessionStorage: der Testschalter muss mit in die URL,
              // sonst startet die Zielseite ohne ihn.
              window.open(withKIshaParam(`${currentUrl}?v=${AppViews.AIASSISTANT}`), "_blank", "noopener,noreferrer");
            }}
            aria-label="KIsha Reiseassistentin"
            title="KIsha Reiseassistentin"
            style={{ filter: "none", color: "var(--color-accent, #ff8c00)", fontSize: "22px" }}
          >
            <MdSmartToy aria-hidden="true" />
          </span>
        )}

        {/* Suchen-Button nur für PAC/ACC (FLT + LTA haben eigene) */}
        {activeMode !== "FLT" && activeMode !== "LTA" && (
          <Button
            text={intl.formatMessage({ id: "search" })}
            size={"l"}
            color={"orange"}
            style={"sharp"}
            onClick={() => {
              if (!searchRegions || searchRegions?.length == 0 || !searchPersons?.adults || !searchDate?.duration || !searchDate?.start || !searchDate?.end) {
                return;
              }

              // iOS Safari Fix: window.open() SOFORT nach Validation, VOR allen Dispatches!
              // iOS hat ein enges Zeitfenster fuer "User Gesture" — Dispatches koennen es sprengen.
              let queryid = uuidv4();
              let currentUrl = agencyproperties?.bookingurl || window.location.origin + window.location.pathname;
              let targetUrl = currentUrl + "?v=" + AppViews.SECTION + "&id=" + queryid + (sessionId ? "&s=" + sessionId : "");
              let newWindow = window.open("about:blank", "_blank");

              dispatch(
                setSearchOption({
                  property: "children",
                  data: {
                    value: searchPersons.children + "",
                    label: searchPersons.children + " Kinder",
                  },
                }),
              );

              dispatch(
                setSearchOption({
                  property: "adults",
                  data: {
                    value: searchPersons.adults + "",
                    label: searchPersons.adults + " Erwachsene",
                  },
                }),
              );

              dispatch(
                setSearchOption({
                  property: "childrenages",
                  data: searchPersons.childrenAges.map((e) => {
                    return {
                      value: e + "",
                      label: e + " Jahre",
                    };
                  }),
                }),
              );

              dispatch(
                setSearchOption({
                  property: "multiroomdivider",
                  data: travelerParamsFromSelection(searchPersons).multiroomdivider,
                }),
              );

              dispatch(
                setSearchOption({
                  property: "roomallocation",
                  data: travelerParamsFromSelection(searchPersons).roomallocation,
                }),
              );

              let travelDate = {
                from: searchDate.start.split(".")[2] + "-" + searchDate.start.split(".")[1] + "-" + searchDate.start.split(".")[0],
                until: searchDate.end.split(".")[2] + "-" + searchDate.end.split(".")[1] + "-" + searchDate.end.split(".")[0],
              };
              dispatch(
                setSearchOption({
                  property: "travelDate",
                  data: {
                    from: searchDate.start.split(".")[2] + "-" + searchDate.start.split(".")[1] + "-" + searchDate.start.split(".")[0],
                    until: searchDate.end.split(".")[2] + "-" + searchDate.end.split(".")[1] + "-" + searchDate.end.split(".")[0],
                  },
                }),
              );

              dispatch(
                setSearchOption({
                  property: "duration",
                  data: {
                    value: searchDate.duration + "",
                    label: getDurationLabel(searchDate.duration, searchDate.durationRange, activeMode === "ACC", intl),
                    weekPreset: searchDate.weekPreset || null,
                    weekdays: searchDate.weekdays || null,
                  },
                }),
              );

              let stations = [...(searchAirports || [])];

              dispatch(
                setSearchOption({
                  property: "stations",
                  data: [...(searchAirports || [])],
                }),
              );

              dispatch(setSelectedDestination(searchRegions));

              let regions = [...searchRegions];

              const allHotels = searchRegions.every((reg) => reg.HotelID && reg.villageName && reg.iffVillageCode);

              let desiredHotels = [];

              if (allHotels) {
                regions = [
                  {
                    CityID: searchRegions[0].iffVillageCode,
                    Name: searchRegions[0].villageName,
                    label: searchRegions[0].villageName,
                    value: searchRegions[0].villageName,
                  },
                ];
                dispatch(setSelectedDestination(regions));
                for (let i = 0; i < searchRegions.length; i++) {
                  desiredHotels.push({
                    HotelID: searchRegions[i].HotelID,
                    Name: searchRegions[i].name,
                    label: searchRegions[i].name,
                    value: searchRegions[i].name,
                  });
                }
              }

              dispatch(
                setSearchOption({
                  property: "desiredHotels",
                  data: [...desiredHotels],
                }),
              );

              try {
                axios
                  .post(getHost() + "/sendQuery", {
                    promosession: sessionId,
                    queryid: queryid,
                    init: 1,
                    searchOptions: {
                      ...searchParameters,
                      mode: activeMode || window.configData?.MODE || "",
                      travelDate: travelDate,
                      duration: {
                        value: searchDate.duration + "",
                        label: getDurationLabel(searchDate.duration, searchDate.durationRange, activeMode === "ACC", intl),
                        weekPreset: searchDate.weekPreset || null,
                        weekdays: searchDate.weekdays || null,
                      },
                      children: {
                        value: searchPersons.children + "",
                        label: searchPersons.children + " Kinder",
                      },
                      adults: {
                        value: searchPersons.adults + "",
                        label: searchPersons.adults + " Erwachsene",
                      },
                      childrenages: searchPersons.childrenAges.map((e) => {
                        return {
                          value: e + "",
                          label: e + " Jahre",
                        };
                      }),
                      desiredHotels: desiredHotels,
                      ...travelerParamsFromSelection(searchPersons),
                      searchDate: searchDate,
                      searchPersons: searchPersons,
                      stations: stations,
                      searchRegions: searchRegions,
                      selectedDestination: regions,
                      preferredCategory: preferredCategory,
                    },
                  })
                  .then(() => {
                    // Nach erfolgreichem API-Call: Ziel-URL im vorgeöffneten Fenster setzen
                    if (newWindow && !newWindow.closed) {
                      newWindow.location.href = targetUrl;
                    } else {
                      // Fallback: iOS hat das Fenster geblockt oder User hat es geschlossen
                      window.location.href = targetUrl;
                    }
                  })
                  .catch((e) => {
                    // Bei Fehler: vorgeöffnetes Fenster aufräumen
                    console.log(e);
                    if (newWindow && !newWindow.closed) {
                      newWindow.close();
                    }
                  });
              } catch (e) {
                console.log(e);
                if (newWindow && !newWindow.closed) {
                  newWindow.close();
                }
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

// Separate Sub-Komponente für Flug-Suchfelder
function FlightSearchInputs({ dispatch, intl }) {
  const view = useSelector((state) => state.system.view);
  const stations = useSelector((state) => state.search.stations);
  const travelDate = useSelector((state) => state.search.travelDate);
  const adults = useSelector((state) => state.search.adults);
  const oneway = useSelector((state) => state.search.oneway);
  const directflight = useSelector((state) => state.search.directflight);

  const fromValue = travelDate?.from?.split("T")[0] ?? "";
  const toValue = travelDate?.until?.split("T")[0] ?? "";

  const adultsOptions = [
    { value: "1", label: "1 Erwachsener" },
    { value: "2", label: "2 Erwachsene" },
    { value: "3", label: "3 Erwachsene" },
    { value: "4", label: "4 Erwachsene" },
  ];

  return (
    <div className="front_search-flight_inputs">
      {/* Airports Row */}
      <div className="front_search-flight_inputs-row">
        <div className="front_search-flight_inputs-airport">
          <div className="front_search-flight_inputs-label">
            <MdFlightTakeoff className="front_search-flight_inputs-icon" />
            <span>{intl.formatMessage({ id: "departureAirport" })}</span>
          </div>
          <AirportIataSelect stationindex={0} />
        </div>
        <div className="front_search-flight_inputs-airport">
          <div className="front_search-flight_inputs-label">
            <MdFlightLand className="front_search-flight_inputs-icon" />
            <span>{intl.formatMessage({ id: "destinationAirport" })}</span>
          </div>
          <AirportIataSelect stationindex={1} />
        </div>
      </div>

      {/* Dates + Options Row */}
      <div className="front_search-flight_inputs-row">
        <div className="front_search-flight_inputs-date">
          <label>{intl.formatMessage({ id: "outboundFlight" })}:</label>
          <input
            type="date"
            value={fromValue}
            onChange={(e) => {
              const val = e.target.value;
              const until = travelDate?.until && val && travelDate.until > val ? travelDate.until : undefined;
              dispatch(setSearchOption({ property: "travelDate", data: { ...travelDate, from: val, until } }));
              if (!until) dispatch(setSearchOption({ property: "oneway", data: true }));
            }}
          />
        </div>
        <div className="front_search-flight_inputs-date">
          <label>{intl.formatMessage({ id: "inboundFlight" })}:</label>
          <input
            type="date"
            value={toValue}
            min={fromValue}
            onChange={(e) => {
              dispatch(setSearchOption({ property: "travelDate", data: { ...travelDate, until: e.target.value } }));
              if (e.target.value) dispatch(setSearchOption({ property: "oneway", data: false }));
            }}
          />
        </div>

        <select
          className="front_search-flight_inputs-select"
          value={adults?.value || "2"}
          onChange={(e) => dispatch(setSearchOption({ property: "adults", data: adultsOptions.find((o) => o.value === e.target.value) }))}
        >
          {adultsOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <label className="front_search-flight_inputs-checkbox">
          <input type="checkbox" checked={oneway || false} onChange={(e) => dispatch(setSearchOption({ property: "oneway", data: e.target.checked }))} />
          Nur Hinflug
        </label>

        <label className="front_search-flight_inputs-checkbox">
          <input type="checkbox" checked={directflight || false} onChange={(e) => dispatch(setSearchOption({ property: "directflight", data: e.target.checked }))} />
          {intl.formatMessage({ id: "directFlight" })}
        </label>

        <Button
          text={intl.formatMessage({ id: "search" })}
          size={"l"}
          color={"orange"}
          style={"sharp"}
          onClick={() => {
            if (view === AppViews.SECTION) {
              // Auf Ergebnisseite: direkt suchen (kein neuer Tab)
              dispatch(fetchFares());
            } else {
              // Auf Startseite: neuer Tab mit URL-Params (wie PAC/ACC)
              const p = new URLSearchParams();
              p.set("v", AppViews.SECTION);
              p.set("mode", "FLT");
              if (stations?.[0]) {
                p.set("dep_iata", stations[0].value);
                p.set("dep_label", stations[0].label);
              }
              if (stations?.[1]) {
                p.set("arr_iata", stations[1].value);
                p.set("arr_label", stations[1].label);
              }
              if (travelDate?.from) p.set("dep_date", travelDate.from);
              if (travelDate?.until) p.set("ret_date", travelDate.until);
              p.set("adults", adults?.value || "2");
              if (oneway) p.set("oneway", "1");
              if (directflight) p.set("direct", "1");
              window.open("?" + p.toString(), "_blank");
            }
          }}
        />
      </div>
    </div>
  );
}

// Separate Sub-Komponente für Erlebnisreisen-Suchfelder
function IndividualSearchInputs({ dispatch, intl }) {
  const kishaVisible = useKIshaVisible();
  const stations = useSelector((state) => state.search.stations);
  const travelDate = useSelector((state) => state.search.travelDate);
  const adults = useSelector((state) => state.search.adults);
  const agencyproperties = useSelector((state) => state.properties.agencyproperties);
  const [selectedCategory, setSelectedCategory] = useState("");

  const fromValue = travelDate?.from?.split("T")[0] ?? "";
  const toValue = travelDate?.until?.split("T")[0] ?? "";

  const adultsOptions = [
    { value: "1", label: "1 Erwachsener" },
    { value: "2", label: "2 Erwachsene" },
    { value: "3", label: "3 Erwachsene" },
    { value: "4", label: "4 Erwachsene" },
  ];

  const categories = [
    { value: "", label: "Alle Kategorien" },
    { value: "Konzerte", label: "Konzerte" },
    { value: "_sep", label: "──────────", disabled: true },
    { value: "Fußball", label: "Fußball" },
    { value: "Tennis", label: "Tennis" },
    { value: "Basketball", label: "Basketball" },
    { value: "Eishockey", label: "Eishockey" },
    { value: "Rugby", label: "Rugby" },
    { value: "Motorsport", label: "Motorsport" },
    { value: "Golf", label: "Golf" },
    { value: "Handball", label: "Handball" },
  ];

  return (
    <div className="front_search-flight_inputs">
      {/* Row 1: Abflughafen + Event-Kategorie */}
      <div className="front_search-flight_inputs-row">
        <div className="front_search-flight_inputs-airport">
          <div className="front_search-flight_inputs-label">
            <MdFlightTakeoff className="front_search-flight_inputs-icon" />
            <span>{intl.formatMessage({ id: "departureAirport" })}</span>
          </div>
          <AirportIataSelect stationindex={0} />
        </div>
        <div className="front_search-flight_inputs-airport">
          <div className="front_search-flight_inputs-label">
            <span>Event / Erlebnis</span>
          </div>
          <select className="front_search-flight_inputs-select" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} style={{ width: "100%", minHeight: 38 }}>
            {categories.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 2: Reisezeit + Reisende + Suchen */}
      <div className="front_search-flight_inputs-row">
        <div className="front_search-flight_inputs-date">
          <label>Reisezeit von:</label>
          <input
            type="date"
            value={fromValue}
            onChange={(e) => {
              const val = e.target.value;
              const until = travelDate?.until && val && travelDate.until > val ? travelDate.until : undefined;
              dispatch(setSearchOption({ property: "travelDate", data: { ...travelDate, from: val, until } }));
            }}
          />
        </div>
        <div className="front_search-flight_inputs-date">
          <label>Reisezeit bis:</label>
          <input
            type="date"
            value={toValue}
            min={fromValue}
            onChange={(e) => {
              dispatch(setSearchOption({ property: "travelDate", data: { ...travelDate, until: e.target.value } }));
            }}
          />
        </div>

        <div className="front_search-flight_inputs-date">
          <label>Reisende:</label>
          <select
            className="front_search-flight_inputs-select"
            value={adults?.value || "2"}
            onChange={(e) => dispatch(setSearchOption({ property: "adults", data: adultsOptions.find((o) => o.value === e.target.value) }))}
          >
            {adultsOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {kishaVisible && (
          <span
            className="front_search-flight_inputs-kisha"
            role="button"
            tabIndex="0"
            onClick={() => {
              const currentUrl = agencyproperties?.bookingurl || window.location.origin + window.location.pathname;
              // Neuer Tab = leerer sessionStorage: der Testschalter muss mit in die URL,
              // sonst startet die Zielseite ohne ihn.
              window.open(withKIshaParam(`${currentUrl}?v=${AppViews.AIASSISTANT}`), "_blank", "noopener,noreferrer");
            }}
            aria-label="KIsha Reiseassistentin"
            title="KIsha Reiseassistentin"
            style={{ marginLeft: "auto" }}
          >
            <MdSmartToy aria-hidden="true" />
          </span>
        )}
        <Button
          text={intl.formatMessage({ id: "search" })}
          size={"l"}
          color={"orange"}
          style={"sharp"}
          onClick={() => {
            // Neuer Tab mit LTA-Params → IndividualSection
            const p = new URLSearchParams();
            p.set("v", AppViews.SECTION);
            p.set("mode", "LTA");
            if (selectedCategory) p.set("category", selectedCategory);
            if (fromValue) p.set("date_from", fromValue);
            if (toValue) p.set("date_to", toValue);
            if (stations?.[0]) {
              p.set("dep_iata", stations[0].value);
              p.set("dep_label", stations[0].label);
            }
            p.set("adults", adults?.value || "2");
            let currentUrl = agencyproperties?.bookingurl || window.location.origin + window.location.pathname;
            window.open(currentUrl + "?" + p.toString(), "_blank");
          }}
        />
      </div>
    </div>
  );
}

export default FrontSearchInputs;
